import { Card, StepProgress } from '@ai-tutor/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BrandBar } from '@/components/core/layout/BrandBar';
import { FullScreenSpinner } from '@/components/core/common/feedback/FullScreenSpinner';
import { LAST_APP_PATH_KEY } from '@/components/core/layout/AppLayout';
import { useSubscription } from '@/hooks/account/useBilling';
import { useCompleteOnboarding, useUserProfile } from '@/hooks/account/useUser';
import {
  resolveLessonPreferences,
  useUserLessonSettings,
} from '@/hooks/account/useUserLessonSettings';
import type { LessonDepth, Plan, UserRole } from '@/lib/api-client';
import { purchaseSummary } from '@/lib/billing';
import { type FlowPreset } from '@/config/lesson-settings-options';
import { track } from '@/config/observability';
import { notify } from '@/lib/notify';

import { CompleteStep } from './steps/CompleteStep';
import { LessonPrefsStep } from './steps/LessonPrefsStep';
import { PricingStep } from './steps/PricingStep';
import { RoleStep } from './steps/RoleStep';

type Step = 'role' | 'lessons' | 'pricing' | 'complete';

/**
 * The steps, in order. `StepProgress` renders these names rather than a bare
 * fraction — seeing `Plan` ahead of time, and that it is followed by `Done`
 * rather than by more forms, answers "am I about to hit a paywall?" before it
 * becomes a reason to abandon the signup.
 *
 * `You` and `Lessons` used to be one screen. They were split because they are
 * different asks wearing the same clothes: role is the one answer with no
 * default, while depth and flow arrive pre-filled. Side by side at identical
 * visual weight, nothing told the user which one actually needed them — and the
 * combined screen overflowed the fixed-height card, so the required question
 * and the Continue button were never on screen together.
 */
const STEPS: { id: Step; label: string }[] = [
  { id: 'role', label: 'You' },
  { id: 'lessons', label: 'Lessons' },
  { id: 'pricing', label: 'Plan' },
  { id: 'complete', label: 'Done' },
];

/**
 * Full-screen onboarding wizard. Rendered by `OnboardingPage` inside the app's
 * GridBackground — the wizard itself is transparent so the grid shows through.
 * The page doesn't scroll: the brand bar is pinned and only the card body
 * scrolls. Question selections live here (not in ProfileStep) so they survive
 * navigating back from Pricing. `onboarded` is flipped only at the final step.
 */
export function OnboardingWizard() {
  const navigate = useNavigate();
  const { data: profile, isLoading } = useUserProfile();
  const lessonSettings = useUserLessonSettings();
  const completeOnboarding = useCompleteOnboarding();
  const [step, setStep] = useState<Step>('role');

  // Selections are owned here so Back from Pricing preserves them. Role is
  // persisted (with the `onboarded` flip) only at the final step.
  const [role, setRole] = useState<UserRole>();

  // Depth + flow are seeded from the SERVER, not from the module defaults, and
  // the local state stays null until the user actually picks something.
  //
  // They have to be: ProfileStep PUTs them on Continue, so a user who chose
  // Deep/Full and then reloaded mid-flow came back to a wizard showing
  // Standard/Core while the server held Deep/Full — and continuing again
  // silently overwrote their real answers with the defaults. Deriving from the
  // query instead of copying into state on load also means a late refetch can
  // never clobber a selection the user has already made.
  const [picked, setPicked] = useState<{
    depth: LessonDepth;
    flow: FlowPreset;
  } | null>(null);
  const { depth, flow } =
    picked ?? resolveLessonPreferences(lessonSettings.data);

  // The plan paid for on the pricing step, so the Complete screen can confirm
  // it. The subscription read only runs after a purchase — checkout has
  // already refreshed it into the cache by then — and until it lands the
  // confirmation falls back to the plan that was sold.
  const [purchased, setPurchased] = useState<Plan | null>(null);
  const subscription = useSubscription({ enabled: purchased !== null });
  const purchase = purchased
    ? purchaseSummary(purchased, subscription.data)
    : undefined;

  const submitOnboarding = (chosen: UserRole) =>
    completeOnboarding.mutate(
      { role: chosen },
      {
        onSuccess: () =>
          track('onboarding_completed', { role: chosen, flow, depth }),
        onError: err => notify.error(err, 'Couldn’t finish setup'),
      }
    );

  // Persist role + flip `onboarded` as soon as step 2 (pricing) is done —
  // whether they continued on Free or completed payment. The Complete screen
  // then reports the result and offers a retry if the write failed.
  const finishPricing = () => {
    if (!role) {
      // Unreachable: RoleStep refuses to advance without a role. If it ever
      // happens, send them back to answer rather than PUT a null role.
      setStep('role');
      return;
    }
    submitOnboarding(role);
    setStep('complete');
  };

  const goToDashboard = () => navigate('/', { replace: true });

  // Spinner-first: we resolve the onboarded flag BEFORE rendering anything, so
  // an already-onboarded user never sees a flash of the wizard. `isLoading` is
  // bounded by the profile query's retry policy (~7s worst case; 401/404 don't
  // retry), but a fail-safe guarantees an upper bound so the spinner can't hang
  // like the old auth bug.
  const [failSafe, setFailSafe] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFailSafe(true), 6000);
    return () => clearTimeout(t);
  }, []);
  // Lesson settings are part of the gate because step 1 renders their saved
  // values as pre-selected tiles; resolving them after paint would show the
  // defaults and then visibly re-select. A failed query resolves this too
  // (`isLoading` is false once the query settles either way), so a user whose
  // settings 404 still gets the wizard, just on the defaults.
  const flagKnown = (!isLoading && !lessonSettings.isLoading) || failSafe;

  // Onboarded users who land on /onboarding are bounced ONE STEP BACK to the
  // route they came from (tracked in sessionStorage by AppLayout, so it
  // survives a hard reload), falling back to home when there's none (fresh tab
  // / direct hit). Only at the initial step, so a mid-flow completion - which
  // flips the flag while we're on the Complete screen - doesn't bounce them
  // off it.
  const alreadyOnboarded = step === 'role' && profile?.onboarded === true;
  useEffect(() => {
    if (!alreadyOnboarded) return;
    const last = sessionStorage.getItem(LAST_APP_PATH_KEY);
    navigate(last && last !== '/onboarding' ? last : '/', { replace: true });
  }, [alreadyOnboarded, navigate]);

  // One event per step actually shown, so the funnel sees where people stop.
  const wizardVisible = flagKnown && !alreadyOnboarded;
  useEffect(() => {
    if (wizardVisible) track('onboarding_step_viewed', { step });
  }, [wizardVisible, step]);

  // Show the spinner (never the wizard) while (1) the flag is still unknown or
  // (2) we're bouncing an onboarded user back. Transparent over GridBackground
  // to match the wizard's own transparent-over-grid design.
  if (!flagKnown || alreadyOnboarded) {
    return <FullScreenSpinner />;
  }

  const stepIndex = STEPS.findIndex(s => s.id === step);

  return (
    <div className='flex h-screen w-full flex-col overflow-hidden'>
      {/* `static`, not fixed: this sits in a flex column that owns the page
          height, so the bar is an in-flow child rather than an overlay. */}
      <BrandBar position='static' />

      {/* Card fills the space below the bar so its height is FIXED across
          steps (page never scrolls; only the card body scrolls).

          `max-w-3xl`, not `5xl`: this is a short form, not a dashboard. At
          1024px the four role tiles stretch to ~460px each to hold a single
          line of copy, and the depth/flow triples get similarly airy. 768px is
          the width OptionTile was proportioned for. It is also the width the
          step stories have always rendered at, so the catalog and the app now
          agree — see `withWizardCard` in stories/decorators.tsx. */}
      <div className='flex min-h-0 flex-1 justify-center px-4 py-6 sm:px-6 lg:px-8'>
        {/* `rounded-2xl`: the tiles inside are `rounded-lg`, so corners step
            DOWN. `elevation='md'` is the token shadow — stock `shadow` paints
            nothing against a #0F1015 page. Kept in step with `withWizardCard`
            in `stories/decorators.tsx`, which renders this same shell; the two
            are the reason `Card` exists rather than a fifth copy of the
            string. */}
        <Card
          elevation='md'
          className='flex h-full w-full max-w-3xl flex-col overflow-hidden rounded-2xl'
        >
          {/* `max-w-md` keeps the four step names from spreading to the
              card's full width, where the row reads as a nav bar. */}
          <div className='shrink-0 border-b border-border/60 px-6 py-4 sm:px-10'>
            <StepProgress
              steps={STEPS}
              currentIndex={stepIndex}
              ariaLabel='Onboarding progress'
              className='mx-auto max-w-md'
            />
          </div>

          {/* The step fills the rest of the card and does NOT scroll here:
              `StepLayout` fixes its heading and its action footer and scrolls
              only the body between them, so the title and the buttons stay
              on screen however tall a step is. No padding at this level -
              each of StepLayout's three regions carries its own. */}
          <div className='flex min-h-0 flex-1 flex-col'>
            <div
              key={step}
              className='flex min-h-0 flex-1 flex-col duration-slow animate-in fade-in-50 slide-in-from-bottom-2'
            >
              {step === 'role' && (
                <RoleStep
                  role={role}
                  onRoleChange={setRole}
                  onContinue={() => setStep('lessons')}
                />
              )}
              {step === 'lessons' && (
                <LessonPrefsStep
                  depth={depth}
                  flow={flow}
                  onDepthChange={d => setPicked({ depth: d, flow })}
                  onFlowChange={f => setPicked({ depth, flow: f })}
                  onContinue={() => setStep('pricing')}
                  onBack={() => setStep('role')}
                />
              )}
              {step === 'pricing' && (
                <PricingStep
                  onFinish={finishPricing}
                  onSubscribed={setPurchased}
                  onBack={() => setStep('lessons')}
                />
              )}
              {step === 'complete' && (
                <CompleteStep
                  depth={depth}
                  flow={flow}
                  onEnter={goToDashboard}
                  loading={completeOnboarding.isPending}
                  failed={completeOnboarding.isError}
                  onRetry={() => role && submitOnboarding(role)}
                  purchase={purchase}
                />
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
